import { type Dtos, formatToPercent } from "@inrev/common";
import { EmptyState } from "@portal/components/patterns";
import { Card, Text } from "@portal/components/primitives";
import { useCallback, useMemo } from "react";
import {
	CartesianGrid,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	type TooltipContentProps,
	XAxis,
	YAxis,
} from "recharts";
import { firstPayloadDatum, isYearRow, type YearRow } from "./recharts-payload";
import { lossRatioYDomain, trimEmptyYears } from "./sfaa-loss-ratio-chart";

type SeriesEntity = Dtos.Admin.SfaaClassExperience.Series.Entity;

type SfaaLossRatioChartProps = {
	title: string;
	caption: string;
	years: readonly number[];
	selected: SeriesEntity;
	comparison: SeriesEntity | null;
	metric: "lossRatio" | "trailingLossRatio";
	testId?: string;
};

const CHART_MARGIN = { top: 8, right: 12, bottom: 0, left: 0 } as const;
const AXIS_TICK = { className: "fill-tertiary text-[11px]" } as const;
const TOOLTIP_CURSOR = { className: "stroke-bc-strong", strokeWidth: 1 } as const;
const TOOLTIP_WRAPPER_STYLE = { outline: "none" } as const;
const GRID = { className: "stroke-bc-default" } as const;
const ACTIVE_DOT = { r: 4, strokeWidth: 2, className: "fill-current stroke-surface-base" } as const;

/** Persistent dots keep isolated years visible when adjacent years have no data. */
const DOT = { r: 2, strokeWidth: 0, className: "fill-current" } as const;

/** A numeric axis preserves the real width of gaps between nonconsecutive years. */
const YEAR_DOMAIN = ["dataMin", "dataMax"] as const;

const MIN_POINTS = 2;

type ChartRow = YearRow;

const percentTick = (value: number) => `${Math.round(value * 100)}%`;

const percentValue = (value: number | null) =>
	value === null ? "No data" : `${(value * 100).toFixed(1)}%`;

const averageLabel = (value: number | null) =>
	value === null ? "—" : formatToPercent(value, 1) || "—";

const yearSpan = (years: readonly number[]): string | null => {
	const first = years[0];
	const last = years[years.length - 1];
	if (first === undefined || last === undefined) return null;
	return `${first}–${last}`;
};

const LegendItem = ({
	label,
	average,
	swatchClassName,
}: {
	label: string;
	average: number | null;
	swatchClassName: string;
}) => (
	<div className="flex items-center gap-1.5">
		<span className={`h-0.5 w-4 shrink-0 rounded-full ${swatchClassName}`} />
		<Text type="caption" color="secondary">
			{label}
		</Text>
		<Text type="caption" color="primary" className="tabular-nums">
			{averageLabel(average)}
		</Text>
	</div>
);

export const SfaaLossRatioChart = ({
	title,
	caption,
	years,
	selected,
	comparison,
	metric,
	testId,
}: SfaaLossRatioChartProps) => {
	const data = useMemo<ChartRow[]>(() => {
		const selectedByYear = new Map(selected.points.map((point) => [point.year, point[metric]]));
		const comparisonByYear = new Map(
			(comparison?.points ?? []).map((point) => [point.year, point[metric]]),
		);

		return trimEmptyYears(
			years.map((year) => ({
				year,
				selected: selectedByYear.get(year) ?? null,
				comparison: comparisonByYear.get(year) ?? null,
			})),
		);
	}, [years, selected.points, comparison?.points, metric]);

	const yDomain = useMemo(
		() => lossRatioYDomain(data.flatMap((row) => [row.selected, row.comparison])),
		[data],
	);

	const plottablePoints = useMemo(
		() => data.filter((row) => row.selected !== null || row.comparison !== null).length,
		[data],
	);

	/** Surface interior gaps because a broken line is otherwise indistinguishable from a render fault. */
	const missingYears = useMemo(
		() => data.filter((row) => row.selected === null && row.comparison === null).length,
		[data],
	);

	const span = yearSpan(data.map((row) => row.year));

	const tooltip = useCallback(
		({ active, payload }: TooltipContentProps) => {
			if (!active || !payload || payload.length === 0) return null;
			const row = firstPayloadDatum(payload, isYearRow);
			if (row === undefined) return null;

			return (
				<div className="rounded border border-bc-default bg-surface-base px-3 py-2 shadow-md">
					<Text type="caption" color="tertiary">
						{row.year}
					</Text>
					<div className="mt-1 flex flex-col gap-2.5">
						<div className="flex items-center gap-2">
							<span className="size-2 shrink-0 rounded-sm bg-brand-accent" />
							<Text type="caption" color="secondary">
								{selected.label}
							</Text>
							<Text type="caption" color="primary" className="ml-auto tabular-nums">
								{percentValue(row.selected)}
							</Text>
						</div>
						{comparison !== null && (
							<div className="flex items-center gap-2">
								<span className="size-2 shrink-0 rounded-sm bg-viz-series-comparison" />
								<Text type="caption" color="secondary">
									{comparison.label}
								</Text>
								<Text type="caption" color="primary" className="ml-auto tabular-nums">
									{percentValue(row.comparison)}
								</Text>
							</div>
						)}
					</div>
				</div>
			);
		},
		[selected.label, comparison],
	);

	return (
		<Card elevation="raised" className="shadow-gray-900/10" data-testid={testId}>
			<Card.Header>
				<Card.Title>{title}</Card.Title>
			</Card.Header>
			<Card.Body className="flex h-[300px] flex-col gap-2 p-4">
				<div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
					<div className="flex flex-col gap-2.5">
						<Text type="caption" color="tertiary">
							{caption}
						</Text>
						{missingYears > 0 && (
							<Text type="caption" color="tertiary" data-testid={`${testId}-gaps`}>
								{`${missingYears} ${missingYears === 1 ? "year has" : "years have"} no data, so the line breaks there.`}
							</Text>
						)}
					</div>
					<div className="flex flex-wrap items-center gap-x-4 gap-y-1">
						<LegendItem
							label={selected.label}
							average={selected.stats[metric].average}
							swatchClassName="bg-brand-accent"
						/>
						{comparison !== null && (
							<LegendItem
								label={comparison.label}
								average={comparison.stats[metric].average}
								swatchClassName="bg-viz-series-comparison"
							/>
						)}
						{span !== null && (
							<Text type="caption" color="tertiary">
								{span}
							</Text>
						)}
					</div>
				</div>

				{plottablePoints < MIN_POINTS ? (
					<EmptyState testId={`${testId}-empty`} message="Not enough history to chart." />
				) : (
					<div className="min-h-0 flex-1">
						<ResponsiveContainer width="100%" height="100%">
							<LineChart data={data} margin={CHART_MARGIN}>
								<CartesianGrid {...GRID} vertical={false} strokeDasharray="2 4" />
								<XAxis
									dataKey="year"
									type="number"
									domain={YEAR_DOMAIN}
									allowDecimals={false}
									tick={AXIS_TICK}
									tickLine={false}
									axisLine={false}
									minTickGap={24}
								/>
								<YAxis
									tick={AXIS_TICK}
									tickLine={false}
									axisLine={false}
									width={44}
									domain={yDomain}
									tickFormatter={percentTick}
								/>
								<Tooltip
									content={tooltip}
									cursor={TOOLTIP_CURSOR}
									wrapperStyle={TOOLTIP_WRAPPER_STYLE}
									isAnimationActive={false}
								/>
								<Line
									className="text-brand-accent"
									type="linear"
									dataKey="selected"
									name={selected.label}
									stroke="currentColor"
									strokeWidth={2}
									strokeLinecap="round"
									dot={DOT}
									activeDot={ACTIVE_DOT}
									connectNulls={false}
									isAnimationActive={false}
								/>
								{comparison !== null && (
									<Line
										className="text-viz-series-comparison"
										type="linear"
										dataKey="comparison"
										name={comparison.label}
										stroke="currentColor"
										strokeWidth={2}
										strokeLinecap="round"
										dot={DOT}
										activeDot={ACTIVE_DOT}
										connectNulls={false}
										isAnimationActive={false}
									/>
								)}
							</LineChart>
						</ResponsiveContainer>
					</div>
				)}
			</Card.Body>
		</Card>
	);
};
